/**
 * Copyright (c) Mayoral Moda Infantil, S.A.U. and affiliates.
 */

import { useReactiveVar } from '@apollo/client';
import { isManagerVar, loggedInVar } from '@graphql/cache';
import { useRouter } from 'next/router';
import { FC, useEffect, useMemo } from 'react';

import {
  AuthorizedManagerRedirectURL,
  AuthorizedRedirectURL,
  UnauthorizedManagerRedirectURL,
  UnauthorizedRedirectURL,
} from '@/lib/constants';

type WithAuthOptions = {
  /** Indicates whether the user is required to be logged in. */
  shouldBeLoggedIn?: boolean;
  /** The Component should be rendered independently of the session state. */
  fallback?: boolean;
};

const defaultOptions: WithAuthOptions = {
  shouldBeLoggedIn: true,
  fallback: true,
};

export const withAuth = (Component: FC, options?: WithAuthOptions) => {
  const optionsWithDefaults = { ...defaultOptions, ...options };

  return function WithAuth(props) {
    const router = useRouter();
    const loggedIn = useReactiveVar(loggedInVar);

    const redirectURL = useMemo(() => {
      if (optionsWithDefaults.shouldBeLoggedIn && !loggedIn) {
        // In case the user is required to be logged in,
        // we redirect to the login page.
        if (isManagerVar()) {
          return UnauthorizedManagerRedirectURL;
        }
        return UnauthorizedRedirectURL;
      } else if (!optionsWithDefaults.shouldBeLoggedIn && loggedIn) {
        // Otherwise, in case the user is logged in or not requerided to be,
        // redirect to the workshop selection or homepage.
        if (isManagerVar()) {
          return AuthorizedManagerRedirectURL;
        }
        return AuthorizedRedirectURL;
      }
    }, [loggedIn]);

    useEffect(() => {
      if (!redirectURL) {
        return;
      }
      router.replace(redirectURL);
    }, [redirectURL]);

    if (!optionsWithDefaults.fallback && redirectURL) {
      return null;
    }

    return <Component {...props} />;
  };
};
